import {useContext} from "react";
import {AppContext, Layer, Shape} from "../../../context/context";
import {defaultLayer} from "../../../context/defaults";
import { logEvent } from "../../../services/analytics";
import { useTranslation } from "react-i18next";

type AddLayerFunction = () => void;

export function useAddLayer(): AddLayerFunction{
    const context = useContext(AppContext);
    const { t } = useTranslation('map');
    if (!context){
        throw new Error("Function useAddLayer must be used within the AppProvider")
    }

    const {global} = context;

    return () => {
        // Use max existing layerId + 1 (not array length) so deleted/imported
        // layers don't cause ID collisions that get deduplicated away.
        const nextLayerId = global.mapLayers.length > 0
            ? Math.max(...global.mapLayers.map(layer => layer.layerId)) + 1
            : 0;

        const newLayer = {
            ...defaultLayer,
            layerId: nextLayerId,
            layerName: t('layers.defaultLayerName'),
        }

        global.setIdCounter(
            (previousValue) => {
                return previousValue + 1
            }
        )

        global.setLayers(previousValue => [
            ...previousValue,
            newLayer
        ])

        // Make the new layer active so drawing tools target it immediately.
        global.setActiveLayerId(newLayer.layerId)

        // Track layer addition
        logEvent({
            category: 'Layer Management',
            action: 'LayerAdded',
            label: newLayer.layerName || 'Unnamed Layer',
            layer_id: newLayer.layerId,
            layer_shape_count: newLayer.shapes.length,
            layer_shape_types: Array.from(new Set(newLayer.shapes.map((s: any) => s.type))).join(','),
            total_layers: global.mapLayers.length + 1
        });
    }
}

type EditLayerFunction = (layer: Layer, layerId: number) => void;

export function useEditLayer(): EditLayerFunction{
    const context = useContext(AppContext);
    if (!context){
        throw new Error("function useEditLayer must be used within the AppProvider")
    }

    const {global} = context;
    const setLayers = global.setLayers

    return (layer: Layer, layerId: Number) => {
        // Get the original layer for comparison
        const originalLayer = global.mapLayers.find(l => l.layerId === layerId);
        
        setLayers(previousValue => {
            let newList: Layer[] = [];

            for (let i =0; i < previousValue.length; i++) {
                const listLayer = previousValue[i];
                if (listLayer.layerId === layerId) {
                    newList.push(layer)

                } else {
                    newList.push(listLayer)
                }
            }
            return newList
        })

        // Track layer editing
        if (originalLayer) {
            const changes = [];
            if (originalLayer.layerName !== layer.layerName) changes.push('name');
            if (originalLayer.hidden !== layer.hidden) changes.push('visibility');
            if (originalLayer.shapes.length !== layer.shapes.length) changes.push('shapes');
            if (originalLayer.expanded !== layer.expanded) changes.push('expanded');

            if (changes.length > 0) {
                logEvent({
                    category: 'Layer Management',
                    action: 'LayerEdited',
                    label: layer.layerName || 'Unnamed Layer',
                    layer_id: layer.layerId,
                    changes_made: changes.join(','),
                    layer_shape_count: layer.shapes.length,
                    layer_shape_types: Array.from(new Set(layer.shapes.map((s: any) => s.type))).join(','),
                    layer_visible: !layer.hidden
                });
            }
        }
    }
}

type DeleteLayerFunction = (layerId: number) => void;

export function useDeleteLayer(): DeleteLayerFunction {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("function useDeleteLayer must be used within the AppProvider");
    }

    const { global } = context;
    const setLayers = global.setLayers;

    return (layerId: number) => {
        // Get the layer being deleted for tracking
        const layerToDelete = global.mapLayers.find(l => l.layerId === layerId);
        
        if (global.mapLayers.length === 1) {
            // don't let them delete the last layer
            return
        }

        // Handle active layer deletion: if deleted layer is active, set first remaining layer as active
        const wasActiveLayer = global.activeLayerId === layerId;
        const remainingLayers = global.mapLayers.filter(l => l.layerId !== layerId);
        if (wasActiveLayer && remainingLayers.length > 0) {
            global.setActiveLayerId(remainingLayers[0].layerId);
        }

        // Clear selected shapes that belong to the deleted layer
        const shapesInDeletedLayer = layerToDelete?.shapes.map(s => s.id) || [];
        if (shapesInDeletedLayer.length > 0) {
            global.setSelectedShapes(prev => prev.filter(id => !shapesInDeletedLayer.includes(id)));
        }

        setLayers(previousValue => {
            let newList: Layer[] = [];

            for (let i = 0; i < previousValue.length; i++) {
                const listLayer = previousValue[i];
                if (listLayer.layerId !== layerId) {
                    newList.push(listLayer); // Keep only layers not matching the id
                }
                // If layerId matches, do not push it (delete it)
            }

            return newList;
        });

        // Track layer deletion
        if (layerToDelete) {
            logEvent({
                category: 'Layer Management',
                action: 'LayerDeleted',
                label: layerToDelete.layerName || 'Unnamed Layer',
                layer_id: layerToDelete.layerId,
                layer_shape_count: layerToDelete.shapes.length,
                layer_shape_types: Array.from(new Set(layerToDelete.shapes.map((s: any) => s.type))).join(','),
                total_layers_remaining: global.mapLayers.length - 1,
                was_active_layer: wasActiveLayer
            });
        }
    };
}

type SetLayerTitleFunction = (value: string) => void;
type EditLayerTitleFunction = (layer: Layer) => SetLayerTitleFunction;


export function useEditLayerTitle(): EditLayerTitleFunction {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("function useEditLayerTitle must be used within the AppProvider")
    }

    const editLayer = useEditLayer();

    return (layer: Layer) => {

        return (value: string) =>  {
        const newLayer: Layer = {
            ...layer,
            layerName: value
            }

        editLayer(newLayer, layer.layerId)

        // Track layer title change specifically
        logEvent({
            category: 'Layer Management',
            action: 'LayerTitleChanged',
            label: value,
            layer_id: layer.layerId,
            previous_title: layer.layerName || 'Unnamed Layer',
            new_title: value,
            layer_shape_count: layer.shapes.length
        });
    }
}

}

type ReorderLayersFunction = (sourceIndex: number, targetIndex: number) => void;

/**
 * Hook to reorder layers in the mapLayers array
 * @returns Function that takes source and target indices and reorders layers
 */
export function useReorderLayers(): ReorderLayersFunction {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("function useReorderLayers must be used within the AppProvider");
    }

    const { global } = context;
    const setLayers = global.setLayers;

    return (sourceIndex: number, targetIndex: number) => {
        if (sourceIndex === targetIndex) return; // No change needed
        
        setLayers(previousValue => {
            const newLayers = [...previousValue];
            const [movedLayer] = newLayers.splice(sourceIndex, 1);
            newLayers.splice(targetIndex, 0, movedLayer);
            
            // Track layer reordering
            logEvent({
                category: 'Layer Management',
                action: 'LayersReordered',
                label: movedLayer.layerName || 'Unnamed Layer',
                layer_id: movedLayer.layerId,
                source_index: sourceIndex,
                target_index: targetIndex,
                total_layers: newLayers.length
            });
            
            return newLayers;
        });
    };
}

type MoveLayerUpFunction = (layerId: number) => void;
type MoveLayerDownFunction = (layerId: number) => void;

/**
 * Hook to move a layer up one position
 * @returns Function that takes a layerId and moves it up
 */
export function useMoveLayerUp(): MoveLayerUpFunction {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("function useMoveLayerUp must be used within the AppProvider");
    }

    const { global } = context;
    const reorderLayers = useReorderLayers();

    return (layerId: number) => {
        const currentIndex = global.mapLayers.findIndex(l => l.layerId === layerId);
        if (currentIndex <= 0) return; // Already at top or not found
        
        reorderLayers(currentIndex, currentIndex - 1);
    };
}

/**
 * Hook to move a layer down one position
 * @returns Function that takes a layerId and moves it down
 */
export function useMoveLayerDown(): MoveLayerDownFunction {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("function useMoveLayerDown must be used within the AppProvider");
    }

    const { global } = context;
    const reorderLayers = useReorderLayers();

    return (layerId: number) => {
        const currentIndex = global.mapLayers.findIndex(l => l.layerId === layerId);
        if (currentIndex < 0 || currentIndex >= global.mapLayers.length - 1) return; // Already at bottom or not found
        
        reorderLayers(currentIndex, currentIndex + 1);
    };
}

type MoveShapeToLayerFunction = (shapeId: string, targetLayerId: number) => void;
type MoveShapesToLayerFunction = (shapeIds: string[], targetLayerId: number) => void;

/**
 * Hook to move a shape from its current layer to a target layer
 * @returns Function that takes a shapeId and targetLayerId and moves the shape
 */
export function useMoveShapeToLayer(): MoveShapeToLayerFunction {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("function useMoveShapeToLayer must be used within the AppProvider");
    }

    const { global } = context;
    const setLayers = global.setLayers;
    const editLayer = useEditLayer();

    return (shapeId: string, targetLayerId: number) => {
        // Find the shape and its current layer
        let sourceLayer: Layer | undefined;
        let shape: Shape | undefined;
        
        for (const layer of global.mapLayers) {
            const foundShape = layer.shapes.find(s => s.id === shapeId);
            if (foundShape) {
                sourceLayer = layer;
                shape = foundShape;
                break;
            }
        }
        
        if (!sourceLayer || !shape) {
            console.error(`Shape ${shapeId} not found in any layer`);
            return;
        }
        
        // Find target layer
        const targetLayer = global.mapLayers.find(l => l.layerId === targetLayerId);
        if (!targetLayer) {
            console.error(`Target layer ${targetLayerId} not found`);
            return;
        }
        
        // Don't move if already in target layer
        if (sourceLayer.layerId === targetLayerId) {
            return;
        }
        
        // Remove shape from source layer
        const updatedSourceLayer = {
            ...sourceLayer,
            shapes: sourceLayer.shapes.filter(s => s.id !== shapeId)
        };
        editLayer(updatedSourceLayer, sourceLayer.layerId);
        
        // Add shape to target layer with updated layerId
        const updatedShape = {
            ...shape,
            layerId: targetLayerId
        };
        const updatedTargetLayer = {
            ...targetLayer,
            shapes: [...targetLayer.shapes, updatedShape]
        };
        editLayer(updatedTargetLayer, targetLayerId);
        
        // Maintain selection state after moving shape
        // Selection is maintained automatically since we're using shape IDs
        
        // Track shape movement
        logEvent({
            category: 'Layer Management',
            action: 'ShapeMovedToLayer',
            label: shape.name || 'Unnamed Shape',
            shape_id: shapeId,
            shape_type: shape.type,
            source_layer_id: sourceLayer.layerId,
            target_layer_id: targetLayerId
        });
    };
}

/**
 * Hook to move multiple shapes to a target layer
 * @returns Function that takes shapeIds array and targetLayerId and moves all shapes
 */
export function useMoveShapesToLayer(): MoveShapesToLayerFunction {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("function useMoveShapesToLayer must be used within the AppProvider");
    }

    const moveShapeToLayer = useMoveShapeToLayer();

    return (shapeIds: string[], targetLayerId: number) => {
        // Move each shape
        shapeIds.forEach(shapeId => {
            moveShapeToLayer(shapeId, targetLayerId);
        });
        
        // Track bulk movement
        logEvent({
            category: 'Layer Management',
            action: 'ShapesMovedToLayer',
            label: `Moved ${shapeIds.length} shapes`,
            shape_count: shapeIds.length,
            target_layer_id: targetLayerId
        });
    };
}

